/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  Button,
  Form,
  FormInstance,
  Input,
  Select,
  Space,
  Table,
  Tooltip,
} from "antd";
import { TablePaginationConfig } from "antd/lib/table";
import _ from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import LicenseDatail from "src/containers/license/components/LicenseDatail";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import {
  LicenseListDetail,
  LicenseListView,
  LicenseListViewJust,
  LicenseListViewNested,
} from "src/models/license";
import { LicenseSeviceClient } from "src/services/LicenseService";
import { getLocale } from "src/utils/locale";

const { Option } = Select;
export interface IdAndCreateTime {
  id: number;
  createTime: string;
  applySource?: number;
}

export default React.memo(function LicenseList() {
  const formatMessage = useFormatMessage();
  const [queryForm] = Form.useForm();

  const [detail, setDetail] = useState({} as LicenseListDetail);
  const [isView, setIsView] = useState(false);

  const {
    dataSource,
    dataSourceJust,
    pagination,
    createTime,
    notExpiredData,
    loading,
    exportLoading,
    getDetailById,
    onSearch,
    onTableChange,
    initCurrentTablePageIndex,
    exportLicenseList,
  } = useLicenseTableDataSource(queryForm);

  const onCheckDetail = useCallback(
    async (id: number) => {
      const detail = await getDetailById(id);
      if (detail) {
        setDetail(detail);
        setIsView(true);
      }
    },
    [getDetailById]
  );

  const onCancel = useCallback(() => {
    setIsView(!isView);
  }, [isView]);

  const { columns, expandedRowRender } = useLicenseTableColumns(
    dataSource,
    notExpiredData,
    onCheckDetail
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  /**
   * 判断对象的值是不是全为空
   */
  const objectValueAllEmpty = useCallback((obj) => {
    let isEmpty = true;
    Object.keys(obj).forEach(function (x) {
      if (obj[x] !== null && obj[x] !== undefined && obj[x] !== "") {
        return (isEmpty = false);
      }
    });
    return isEmpty;
  }, []);

  const onCleanQueryConditions = useCallback(
    (obj) => {
      const isEmpty = objectValueAllEmpty(obj);
      if (isEmpty) {
        initCurrentTablePageIndex();
      }
    },
    [initCurrentTablePageIndex, objectValueAllEmpty]
  );

  const exportDebounced = _.debounce(() => exportLicenseList(), 200);

  return (
    <div
      css={css`
        flex-grow: 1;
        padding: 30px;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-right: 30px;
          padding-bottom: 30px;
        `}
      >
        <div
          css={css`
            max-width: 1000px;
            margin-right: 20px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `}
        >
          <h1
            css={css`
              position: relative;
              margin: 0px 0px 8px;
              padding-left: 10px;
              line-height: 18px;
              font-size: 18px;
              font-weight: 600;
              color: rgb(61, 73, 102);
              ::before {
                content: "";
                position: absolute;
                left: 0px;
                width: 3px;
                height: 18px;
                background: rgb(51, 119, 255);
              }
            `}
          >
            {formatMessage(messageIds.menu.license.list.name)}
          </h1>
        </div>
      </div>
      <div
        css={css`
          display: flex;
          padding-bottom: 24px;
          border-bottom: 1px solid rgb(202, 209, 224);
          justify-content: space-between;
        `}
      >
        <Form
          layout="inline"
          form={queryForm}
          onValuesChange={(changedValues, allValues) => {
            onCleanQueryConditions(changedValues);
          }}
          onFinish={() => onSearch(1)}
          onFinishFailed={onFinishFailed}
        >
          <Space size={[0, 16]} wrap>
            {/* 客户名称 */}
            <Form.Item
              name={"licensedToCompanyName"}
              label={`${formatMessage(
                messageIds.license.common.licensedToCompanyName
              )}：`}
            >
              <Input
                placeholder={formatMessage(
                  messageIds.license.validate.licensedToCompanyName
                )}
                css={css`
                  width: 180px !important;
                `}
                allowClear
              ></Input>
            </Form.Item>
            {/* 合同号 */}
            <Form.Item
              name={"contractId"}
              label={`${formatMessage(messageIds.license.common.contractId)}：`}
            >
              <Input
                placeholder={formatMessage(
                  messageIds.license.validate.contractId
                )}
                css={css`
                  width: 180px !important;
                `}
                allowClear
              ></Input>
            </Form.Item>
            {/* 申请用途 */}
            <Form.Item
              name={"application"}
              label={`${formatMessage(
                messageIds.license.common.purposeOfApplication
              )}：`}
            >
              <Select
                placeholder={formatMessage(
                  messageIds.license.validate.purposeOfApplication
                )}
                allowClear
                css={css`
                  width: 180px !important;
                `}
              >
                <Option value={1}>POC</Option>
                <Option value={2}>
                  {formatMessage(messageIds.license.common.trial)}
                </Option>
                <Option value={3}>
                  {formatMessage(messageIds.license.common.formal)}
                </Option>
                <Option value={4}>
                  {formatMessage(messageIds.license.common.test)}
                </Option>
              </Select>
            </Form.Item>
            {/* 许可证期效 */}
            <Form.Item
              name={"expireOptions"}
              label={`${formatMessage(messageIds.license.common.validity)}：`}
            >
              <Select
                placeholder={formatMessage(
                  messageIds.license.validate.validity
                )}
                allowClear
                css={css`
                  width: 180px !important;
                `}
              >
                <Option value={0}>
                  {formatMessage(messageIds.license.common.all)}
                </Option>
                <Option value={1}>
                  {formatMessage(messageIds.license.common.sevenDayExpire)}
                </Option>
                <Option value={2}>
                  {formatMessage(messageIds.license.common.oneMonthExpire)}
                </Option>
                <Option value={3}>
                  {formatMessage(messageIds.license.common.threeMonthExpire)}
                </Option>
                <Option value={4}>
                  {formatMessage(messageIds.license.common.expired)}
                </Option>
              </Select>
            </Form.Item>
            {/* 许可证类型 */}
            <Form.Item
              name={"licenseType"}
              label={`${formatMessage(
                messageIds.license.common.licenseType
              )}：`}
            >
              <Select
                placeholder={formatMessage(
                  messageIds.license.validate.licenseType
                )}
                allowClear
                css={css`
                  width: 180px !important;
                `}
              >
                <Option value={"console"}>
                  {formatMessage(messageIds.menu.license.console.name)}
                </Option>
                <Option value={"robot-premium"}>
                  {formatMessage(messageIds.menu.license.premiumRobot.name)}
                </Option>
                <Option value={"studio"}>
                  {formatMessage(messageIds.menu.license.studio.name)}
                </Option>
              </Select>
            </Form.Item>
            {/* 搜索按钮 */}
            <Form.Item>
              <Button type="primary" loading={loading} htmlType="submit">
                {formatMessage(messageIds.common.select)}
              </Button>
            </Form.Item>
          </Space>
        </Form>
        {/* 导出Table */}
        <Button
          type="primary"
          onClick={exportDebounced}
          loading={exportLoading}
        >
          {formatMessage(messageIds.license.common.export)}
        </Button>
      </div>
      <div
        css={css`
          margin-top: 30px;
        `}
      >
        <Table
          columns={columns}
          dataSource={dataSourceJust}
          rowKey={(record) => record?.licensedToCompanyName as string}
          expandable={{ expandedRowRender }}
          onChange={(pagination) => onTableChange(pagination)}
          pagination={{
            ...pagination,
            showSizeChanger: true,
          }}
          loading={loading}
        />
      </div>
      <LicenseDatail
        isView={isView}
        detail={detail}
        idandcreateTime={createTime}
        onCancel={onCancel}
      />
    </div>
  );
});

function useLicenseTableDataSource(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryForm: FormInstance<any>
) {
  const formatMessage = useFormatMessage();
  const [dataSourceJust, setDataSourceJust] = useState<LicenseListViewJust[]>(
    []
  );
  const [dataSource, setDataSource] = useState<LicenseListView[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pagination, setPagiation] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [createTime, setCreateTime] = useState<IdAndCreateTime[]>([]);
  const [notExpiredData, setNotExpiredData] = useState<
    {
      licensedToCompanyName: string | undefined;
      notExpiredCount: number;
    }[]
  >([]);

  const isUnmounted = useRef(false);

  const matchData = useCallback(
    (requestData) => {
      if (isUnmounted.current === false) {
        let allData: LicenseListView[] = [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        allData = requestData.data.sort(function (a: any, b: any) {
          return a.lastCreateTime < b.lastCreateTime ? 1 : -1;
        });
        const dataJust: LicenseListViewJust[] = [];
        const idandcreateTime = [] as IdAndCreateTime[];
        const notExpired: {
          licensedToCompanyName: string | undefined;
          notExpiredCount: number;
        }[] = [];
        // eslint-disable-next-line array-callback-return
        allData.map((data) => {
          const listJust = {
            licensedToCompanyName: data.licensedToCompanyName,
            lastCreateTime: data.lastCreateTime,
            licensesCount: data.licensesCount,
          };
          dataJust.push(listJust);
          let count = 0;
          // eslint-disable-next-line array-callback-return
          data.licenseDataList?.map((item) => {
            idandcreateTime.push({
              id: item.id as number,
              createTime: item.createTime as string,
              applySource: item.applySource as number,
            });
            if (
              moment(item.notAfter).format("YYYY-MM-DD HH:mm:ss") <
              moment().format("YYYY-MM-DD HH:mm:ss")
            ) {
              count = count + 1;
            }
          });
          notExpired.push({
            licensedToCompanyName: data.licensedToCompanyName,
            notExpiredCount: Number(data.licensesCount) - count,
          });
        });

        setPagiation({
          ...pagination,
          total: requestData.dataCount,
        });
        setCreateTime(idandcreateTime);
        setDataSourceJust(dataJust);
        setDataSource(allData);
        setNotExpiredData(notExpired);

        setLoading(false);
        isUnmounted.current = !isUnmounted.current;
      }
    },
    [pagination]
  );

  const fetch = useCallback(
    async (
      pageIndex?: number,
      pageSize?: number,
      licensedToCompanyName?: string,
      contractId?: string,
      application?: number,
      expireOptions?: number,
      licenseType?: string
    ) => {
      setLoading(true);
      const data = await LicenseSeviceClient.licenseList(
        pageIndex,
        pageSize,
        _.trim(licensedToCompanyName),
        contractId,
        application,
        expireOptions,
        licenseType
      );
      const requestData: {
        data: LicenseListView;
        dataCount: number;
        pageCount: number;
        pageIndex: number;
        pageSize: number;
      } = data.data.data;

      matchData(requestData);
      setLoading(false);
    },
    [matchData]
  );

  const onSearch = useCallback(
    async (pageIndex?: number) => {
      isUnmounted.current = !isUnmounted.current;
      const data = await queryForm.validateFields();
      const {
        licensedToCompanyName,
        contractId,
        application,
        expireOptions,
        licenseType,
      } = data;
      fetch(
        pageIndex ?? pagination.current,
        pagination.pageSize,
        licensedToCompanyName,
        contractId,
        application,
        expireOptions,
        licenseType
      );
    },
    [fetch, pagination, queryForm]
  );

  const getDetailById = useCallback(
    async (id: number): Promise<LicenseListDetail> => {
      return await (
        await LicenseSeviceClient.licenseDetail(id)
      ).data.data;
    },
    []
  );

  const handleDownloadData = useCallback(
    (res, queryInputs) => {
      const {
        licensedToCompanyName,
        contractId,
        application,
        expireOptions,
        licenseType,
      } = queryInputs;

      let applicationStr = undefined;
      let expireOptionsStr = undefined;

      switch (application) {
        case 1:
          applicationStr = "POC";
          break;
        case 2:
          applicationStr = formatMessage(messageIds.license.common.trial);
          break;
        case 3:
          applicationStr = formatMessage(messageIds.license.common.formal);
          break;
        case 4:
          applicationStr = formatMessage(messageIds.license.common.test);
          break;
      }

      switch (expireOptions) {
        case 0:
          expireOptionsStr = formatMessage(messageIds.license.common.all);
          break;
        case 1:
          expireOptionsStr = formatMessage(
            messageIds.license.common.sevenDayExpire
          );
          break;
        case 2:
          expireOptionsStr = formatMessage(
            messageIds.license.common.oneMonthExpire
          );
          break;
        case 3:
          expireOptionsStr = formatMessage(
            messageIds.license.common.threeMonthExpire
          );
          break;
        case 4:
          expireOptionsStr = formatMessage(messageIds.license.common.expired);
          break;
      }

      if (!res) {
        return;
      }

      const fileURL = window.URL.createObjectURL(new Blob([res.data]));
      const fileLink = document.createElement("a");
      fileLink.href = fileURL;

      const date = new Date();
      const year = date.getFullYear().toString();
      const month = (date.getMonth() + 1).toString();
      const day = date.getDate().toString();

      fileLink.setAttribute(
        "download",
        `ExportData[${year.concat("-", month, "-", day)}]${
          !_.isEmpty(licensedToCompanyName)
            ? "_".concat(licensedToCompanyName)
            : ""
        }${!_.isEmpty(contractId) ? "_".concat(contractId) : ""}${
          applicationStr !== undefined ? "_".concat(applicationStr) : ""
        }${expireOptionsStr !== undefined ? "_".concat(expireOptionsStr) : ""}${
          !_.isEmpty(licenseType) ? "_".concat(licenseType) : ""
        }.xlsx`
      );
      document.body.appendChild(fileLink);

      fileLink.click();
      URL.revokeObjectURL(fileLink.href);
      document.body.removeChild(fileLink);
      setExportLoading(false);
    },
    [formatMessage]
  );

  const exportLicenseList = useCallback(async () => {
    setExportLoading(true);
    const data = await queryForm.validateFields();
    const {
      licensedToCompanyName,
      contractId,
      application,
      expireOptions,
      licenseType,
    } = data;
    const res = await LicenseSeviceClient.exportLicenseList(
      _.trim(licensedToCompanyName),
      contractId,
      application,
      expireOptions,
      licenseType
    );
    handleDownloadData(res, data);
  }, [handleDownloadData, queryForm]);

  const init = useCallback(async () => {
    fetch(pagination.current, pagination.pageSize);
  }, [fetch, pagination]);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTableChange = useCallback(
    (tablePagination: TablePaginationConfig) => {
      setPagiation({
        ...pagination,
        current: tablePagination.current,
        pageSize: tablePagination.pageSize,
      });
    },
    [pagination]
  );

  const initCurrentTablePageIndex = useCallback(() => {
    setPagiation({
      ...pagination,
      current: 1,
    });
  }, [pagination]);

  useEffect(() => {
    if (pagination && isUnmounted.current) {
      onSearch();
    }
  }, [onSearch, pagination]);

  return {
    dataSource,
    dataSourceJust,
    pagination,
    createTime,
    notExpiredData,
    loading,
    exportLoading,
    getDetailById,
    onSearch,
    onTableChange,
    initCurrentTablePageIndex,
    exportLicenseList,
  };
}

function useLicenseTableColumns(
  dataSource: LicenseListView[],
  notExpiredData: {
    licensedToCompanyName: string | undefined;
    notExpiredCount: number;
  }[],
  onCheckDetail: (id: number) => Promise<void>
) {
  const formatMessage = useFormatMessage();

  const [nestedPagination, setNestedPagination] =
    useState<TablePaginationConfig>({
      current: 1,
      pageSize: 10,
    });

  const onNestedTableChange = useCallback(
    (nestedTablePagination: TablePaginationConfig) => {
      setNestedPagination({
        current: nestedTablePagination.current,
        pageSize: nestedTablePagination.pageSize,
      });
    },
    []
  );

  const expandedRowRender = useCallback(
    (record: LicenseListViewJust) => {
      const columns = [
        {
          title: formatMessage(messageIds.license.common.licenseType),
          dataIndex: "licenseType",
          width: "15%",
          render: (text: string, record: LicenseListViewNested) => (
            <span
              style={{
                color: `${
                  moment(record.notAfter).format("YYYY-MM-DD HH:mm:ss") <
                    moment().format("YYYY-MM-DD HH:mm:ss") && "rgb(143,155,179)"
                }`,
                textDecoration: `${
                  moment(record.notAfter).format("YYYY-MM-DD HH:mm:ss") <
                    moment().format("YYYY-MM-DD HH:mm:ss") && "line-through"
                }`,
              }}
            >
              <span>
                {text === "console"
                  ? formatMessage(messageIds.menu.license.console.name)
                  : text === "robot-premium"
                  ? formatMessage(messageIds.menu.license.premiumRobot.name)
                  : text === "ocr-local"
                  ? formatMessage(messageIds.menu.license.localOCR.name)
                  : formatMessage(messageIds.menu.license.studio.name)}
              </span>
            </span>
          ),
        },
        {
          title: formatMessage(messageIds.license.common.contractId),
          dataIndex: "contractId",
          width: "15%",
          render: (text: string, record: LicenseListViewNested) => (
            <span>
              {text === "null"
                ? getLocale() === "zh-Hans"
                  ? formatMessage(messageIds.common.empty)
                  : text
                : text}
            </span>
          ),
        },
        {
          title: formatMessage(messageIds.license.common.requestedBy),
          dataIndex: "requestedBy",
          width: "15%",
          render: (text: string, record: LicenseListViewNested) => (
            <span
              style={{
                color: `${
                  moment(record.notAfter).format("YYYY-MM-DD HH:mm:ss") <
                    moment().format("YYYY-MM-DD HH:mm:ss") && "rgb(143,155,179)"
                }`,
                textDecoration: `${
                  moment(record.notAfter).format("YYYY-MM-DD HH:mm:ss") <
                    moment().format("YYYY-MM-DD HH:mm:ss") && "line-through"
                }`,
              }}
            >
              {text}
            </span>
          ),
        },
        {
          title: formatMessage(messageIds.license.common.fromTo),
          dataIndex: "fromTo",
          width: "30%",
          render: (text: string, record: LicenseListViewNested) => (
            <span
              style={{
                color: `${
                  moment(record.notAfter).format("YYYY-MM-DD HH:mm:ss") <
                    moment().format("YYYY-MM-DD HH:mm:ss") && "rgb(143,155,179)"
                }`,
                textDecoration: `${
                  moment(record.notAfter).format("YYYY-MM-DD HH:mm:ss") <
                    moment().format("YYYY-MM-DD HH:mm:ss") && "line-through"
                }`,
              }}
            >
              {moment(record.notBefore).format("YYYY-MM-DD HH:mm:ss")} ~
              {moment(record.notAfter).format("YYYY-MM-DD HH:mm:ss")}
            </span>
          ),
        },
        {
          title: formatMessage(messageIds.license.common.purposeOfApplication),
          dataIndex: "application",
          width: "15%",
          render: (text: number, record: LicenseListViewNested) => (
            <span
              style={{
                color: `${
                  moment(record.notAfter).format("YYYY-MM-DD HH:mm:ss") <
                    moment().format("YYYY-MM-DD HH:mm:ss") && "rgb(143,155,179)"
                }`,
                textDecoration: `${
                  moment(record.notAfter).format("YYYY-MM-DD HH:mm:ss") <
                    moment().format("YYYY-MM-DD HH:mm:ss") && "line-through"
                }`,
              }}
            >
              {text === 1
                ? "POC"
                : text === 2
                ? formatMessage(messageIds.license.common.trial)
                : text === 3
                ? formatMessage(messageIds.license.common.formal)
                : text === 4
                ? formatMessage(messageIds.license.common.test)
                : ""}
            </span>
          ),
        },
        {
          title: formatMessage(messageIds.license.common.issuanceTime),
          dataIndex: "createTime",
          width: "15%",
          render: (text: string, record: LicenseListViewNested) => (
            <span
              style={{
                color: `${
                  moment(record.notAfter).format("YYYY-MM-DD HH:mm:ss") <
                    moment().format("YYYY-MM-DD HH:mm:ss") && "rgb(143,155,179)"
                }`,
                textDecoration: `${
                  moment(record.notAfter).format("YYYY-MM-DD HH:mm:ss") <
                    moment().format("YYYY-MM-DD HH:mm:ss") && "line-through"
                }`,
              }}
            >
              {moment(record.createTime).format("YYYY-MM-DD HH:mm:ss")}
            </span>
          ),
        },
        {
          title: formatMessage(messageIds.common.operation),
          dataIndex: "operation",
          width: "10%",
          render: (text: string, record: LicenseListViewNested) => (
            <Button
              type="link"
              css={css`
                padding-left: 0;
              `}
              onClick={() => onCheckDetail(record.id)}
            >
              {formatMessage(messageIds.common.detail)}
            </Button>
          ),
        },
      ];

      const dataNested: LicenseListViewNested[] = [];
      dataSource
        .filter(
          (item) => item.licensedToCompanyName === record.licensedToCompanyName
        )
        // eslint-disable-next-line array-callback-return
        .map((data) => {
          data.licenseDataList
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ?.sort(function (a: any, b: any) {
              return a.createTime < b.createTime ? 1 : -1;
            })
            // eslint-disable-next-line array-callback-return
            .map((list) => {
              dataNested.push(list);
            });
        });
      return (
        <Table
          columns={columns}
          dataSource={dataNested}
          rowKey={(record) => record?.id as number}
          pagination={
            dataNested.length < 10
              ? false
              : {
                  ...nestedPagination,
                }
          }
          onChange={(pagination) => onNestedTableChange(pagination)}
        />
      );
    },
    [
      dataSource,
      formatMessage,
      nestedPagination,
      onNestedTableChange,
      onCheckDetail,
    ]
  );

  const columns = useMemo(
    () => [
      {
        title: formatMessage(messageIds.license.common.licensedToCompanyName),
        dataIndex: "licensedToCompanyName",
        ellipsis: true,
      },
      {
        title: formatMessage(messageIds.license.common.lastIssuanceTime),
        dataIndex: "lastCreateTime",
        render: (text: string) => moment(text).format("YYYY-MM-DD HH:mm:ss"),
      },
      {
        title: formatMessage(messageIds.license.common.countsOfApplication),
        dataIndex: "licensesCount",
        render: (text: number, record: LicenseListViewJust) => (
          <Tooltip
            title={`${formatMessage(
              messageIds.license.common.notExpired
            )} / ${formatMessage(messageIds.license.common.total)}`}
          >
            {`${
              notExpiredData.filter(
                (item) =>
                  item.licensedToCompanyName === record.licensedToCompanyName
              )[0]?.notExpiredCount
            }
                / ${text}`}
          </Tooltip>
        ),
      },
    ],
    [formatMessage, notExpiredData]
  );

  return {
    columns,
    expandedRowRender,
  };
}
